import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/detalhe/ajustar-item-sheet.tsx — Fase 12, Plano 31 (PV-07, D-28, D-29; T-12-46..T-12-49).
// FormDialog "Ajustar item {n}" de item JÁ RECEBIDO (pv:ajustar): quantidade (nunca abaixo do recebido), preço (manual ou
// "Reprecificar pela tabela"), prazo, prioridade e troca de cor/material. Motivo obrigatório em tudo (auditoria).
// A API informa se a troca é permitida (`trocaMaterialPermitida`, derivado de Apontamento/Sessão/Inspeção); quando
// bloqueada o material fica desabilitado e a cópia de D-29 aparece. O servidor revalida sob lock mesmo assim.
// Ajustar e reprecificar são auditados mas NÃO destrutivos (botão primário/secundário); só a troca usa o botão vermelho,
// na confirmação. Conflito de versão preserva o digitado e oferece "Recarregar PV".
import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { MaterialLookupPv, PedidoVendaItemView, PedidoVendaView, PrioridadePv } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Textarea } from '../../../components/ui/textarea';
import { MaterialCombobox } from '../../../components/vendas/material-combobox';
import { RoteiroCombobox } from '../../../components/vendas/roteiro-combobox';
import {
  COPIA_TROCA_PENDENTE,
  COPIA_TROCAR_MATERIAL,
  ajudaQuantidadeMinima,
  diferencasAjuste,
  motivoValido,
  rotuloReprecificar,
  trocaMaterialPermitida,
  validarQuantidadeAjuste,
} from '../../../lib/vendas/ajustes';
import { erroPrecoPv, focarCampo } from '../../../lib/vendas/validacao-pv';
import { lerNumeroDigitado, paraTexto } from '../../../lib/vendas/numero';
import { usePvComandos, type ResultadoComandoPv } from '../use-pv-comandos';
import { AlertaConflitoPv } from './alerta-conflito-pv';
import { ConfirmacaoDestrutivaDialog } from './confirmacao-destrutiva-dialog';
import { CampoSelect } from '../../../components/ui/campo-select';

/** Cópias do contrato (UI-SPEC § Detalhe do PV). */
export const COPIA_TROCA_BLOQUEADA = 'Troca bloqueada: esta OP já tem etapa apontada. Trate como não conformidade ou retrabalho.';
export const COPIA_SEM_PRECO_VIGENTE = 'Não há preço vigente na tabela para este item.';

interface AjustarItemSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pv: PedidoVendaView;
  /** Item em ajuste (versão atual vinda do PV carregado). */
  readonly item: PedidoVendaItemView | null;
  /** Chamado com o PV devolvido pela API após qualquer ação bem-sucedida. */
  readonly onAtualizado: (pv: PedidoVendaView) => void;
  readonly onRecarregar: () => Promise<PedidoVendaView | null>;
  readonly onFalha: (mensagem: string) => void;
}

const PRIORIDADES: ReadonlyArray<{ valor: PrioridadePv; rotulo: string }> = [
  { valor: 0, rotulo: 'Normal' },
  { valor: 10, rotulo: 'Alta' },
  { valor: 20, rotulo: 'Urgente' },
];

type ErrosAjuste = Partial<Record<'quantidade' | 'preco' | 'motivo' | 'nada', string>>;

interface Campos {
  quantidade: string;
  preco: string;
  previsao: string;
  prioridade: PrioridadePv;
  motivo: string;
  material: MaterialLookupPv | null;
  cor: string;
  roteiro: string | null;
}

function materialDoItem(item: PedidoVendaItemView): MaterialLookupPv | null {
  return item.materialId ? { id: item.materialId, codigo: item.materialCodigo ?? '', nome: item.materialNome ?? '' } : null;
}

function camposDoItem(item: PedidoVendaItemView, pv: PedidoVendaView, motivo = ''): Campos {
  return {
    quantidade: paraTexto(item.quantidade),
    preco: item.precoUnitario === null ? '' : paraTexto(item.precoUnitario),
    previsao: item.previsaoEntrega ?? '',
    prioridade: item.prioridade ?? pv.prioridade,
    motivo,
    material: materialDoItem(item),
    cor: item.cor ?? '',
    roteiro: item.roteiroId,
  };
}

export function AjustarItemSheet({ aberto, onAbertoChange, pv, item, onAtualizado, onRecarregar, onFalha }: AjustarItemSheetProps) {
  const { ajustarItem, reprecificarItem, trocarMaterial } = usePvComandos();
  const [campos, setCampos] = useState<Campos | null>(item ? camposDoItem(item, pv) : null);
  const [erros, setErros] = useState<ErrosAjuste>({});
  const [conflito, setConflito] = useState<string | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [reprecificando, setReprecificando] = useState(false);
  const [recarregando, setRecarregando] = useState(false);
  const [confirmarTroca, setConfirmarTroca] = useState(false);
  // WR-D1-13: aviso de troca de cor/material digitada e não aplicada (o Salvar ajuste não a envia).
  const [avisoTrocaPendente, setAvisoTrocaPendente] = useState(false);
  const itemId = item?.id ?? null;

  // Reinicia só ao abrir (ou trocar de item): recarregar o PV por trás não pode apagar o que foi digitado.
  useEffect(() => {
    if (!aberto || !item) return;
    setCampos(camposDoItem(item, pv));
    setErros({});
    setConflito(null);
    setErroEnvio(null);
    setConfirmarTroca(false);
    setAvisoTrocaPendente(false);
  }, [aberto, itemId]);

  if (!item || !campos) return null;

  const bloqueada = !trocaMaterialPermitida({ algumaEtapaApontada: !item.trocaMaterialPermitida });
  const materialAlvo = campos.material ?? materialDoItem(item);
  const materialMudou = (campos.material?.id ?? null) !== item.materialId;
  const corMudou = (campos.cor.trim() || null) !== item.cor;
  const roteiroMudou = campos.roteiro !== item.roteiroId;
  const podeTrocar = !bloqueada && materialAlvo !== null && (materialMudou || corMudou || roteiroMudou);
  const prioridadeAtual = item.prioridade ?? pv.prioridade;
  const semPrecoVigente = item.precoTabelaAtual === null;

  function alterar<K extends keyof Campos>(chave: K, valor: Campos[K]): void {
    setCampos((atuais) => (atuais ? { ...atuais, [chave]: valor } : atuais));
    if (chave === 'material' || chave === 'cor' || chave === 'roteiro') setAvisoTrocaPendente(false);
  }

  /** Resposta comum das ações: ok/conflito/erro. Devolve o PV quando deu certo. */
  function tratar(resultado: ResultadoComandoPv): PedidoVendaView | null {
    if (resultado.tipo === 'ok') {
      setConflito(null);
      onAtualizado(resultado.resultado);
      return resultado.resultado;
    }
    if (resultado.tipo === 'conflito') {
      setConflito(resultado.message);
    } else {
      setErroEnvio(resultado.message);
      onFalha(resultado.message);
    }
    return null;
  }

  async function salvar(event?: FormEvent, descartarTroca = false): Promise<void> {
    event?.preventDefault();
    if (!item || !campos) return;
    setErroEnvio(null);
    const proximos: ErrosAjuste = {};
    const quantidade = lerNumeroDigitado(campos.quantidade);
    const erroQuantidade = validarQuantidadeAjuste(quantidade, item.saldos.recebido, item.unidade, campos.quantidade);
    if (erroQuantidade) proximos.quantidade = erroQuantidade;
    const preco = campos.preco.trim() === '' ? null : lerNumeroDigitado(campos.preco);
    const erroPreco = preco === null ? null : erroPrecoPv(preco, campos.preco);
    if (erroPreco) proximos.preco = erroPreco;
    if (!motivoValido(campos.motivo)) proximos.motivo = 'Informe o motivo.';
    const diferencas = diferencasAjuste(
      { quantidade: item.quantidade, precoUnitario: item.precoUnitario, previsaoEntrega: item.previsaoEntrega, prioridade: prioridadeAtual },
      { quantidade, preco, previsao: campos.previsao || null, prioridade: campos.prioridade },
    );
    if (Object.keys(proximos).length === 0 && Object.keys(diferencas).length === 0) proximos.nada = 'Altere ao menos um campo do item.';
    setErros(proximos);
    if (proximos.quantidade) return focarCampo('ajuste-quantidade');
    if (proximos.preco) return focarCampo('ajuste-preco');
    if (proximos.motivo) return focarCampo('ajuste-motivo');
    if (proximos.nada) return;
    // Há troca de cor/material/roteiro digitada e não aplicada: não a descarta em silêncio.
    if (podeTrocar && !descartarTroca) {
      setAvisoTrocaPendente(true);
      return;
    }

    setSalvando(true);
    try {
      const atualizado = tratar(await ajustarItem(pv.id, item.id, { ...diferencas, versao: item.versao, motivo: campos.motivo.trim() }));
      if (atualizado) onAbertoChange(false);
    } finally {
      setSalvando(false);
    }
  }

  async function reprecificar(): Promise<void> {
    if (!item || !campos) return;
    setErroEnvio(null);
    if (!motivoValido(campos.motivo)) {
      setErros((atuais) => ({ ...atuais, motivo: 'Informe o motivo.' }));
      focarCampo('ajuste-motivo');
      return;
    }
    setReprecificando(true);
    try {
      const atualizado = tratar(await reprecificarItem(pv.id, item.id, { versao: item.versao, motivo: campos.motivo.trim() }));
      const itemNovo = atualizado?.itens.find((candidato) => candidato.id === item.id);
      if (itemNovo) {
        alterar('preco', itemNovo.precoUnitario === null ? '' : paraTexto(itemNovo.precoUnitario));
        setErros({});
      }
    } finally {
      setReprecificando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      const atual = await onRecarregar();
      const itemAtual = atual?.itens.find((candidato) => candidato.id === itemId);
      if (atual && itemAtual) {
        setCampos(camposDoItem(itemAtual, atual, campos?.motivo ?? ''));
        setErros({});
        setConflito(null);
        setErroEnvio(null);
      } else {
        setErroEnvio('Não foi possível recarregar o PV. Tente novamente.');
      }
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <>
      <FormDialog open={aberto} onOpenChange={onAbertoChange}>
        <FormDialogContent>
          <form onSubmit={(e) => void salvar(e)} noValidate className="flex h-full flex-col">
            <FormDialogHeader>
              <FormDialogTitle className="text-xl">Ajustar item {item.numeroItem}</FormDialogTitle>
              <FormDialogDescription>
                Item já recebido: {item.codigoItemCliente}. Toda alteração fica registrada com o motivo e chega à produção.
              </FormDialogDescription>
            </FormDialogHeader>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
              {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}
              {avisoTrocaPendente && (
                <Alert role="alert" className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTriangle aria-hidden="true" />
                  <AlertDescription className="text-amber-900">
                    <p>{COPIA_TROCA_PENDENTE}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => setConfirmarTroca(true)} disabled={salvando}>
                        Trocar cor/material agora
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => void salvar(undefined, true)} disabled={salvando}>
                        Descartar a troca e salvar
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ajuste-quantidade">Quantidade solicitada</Label>
                <Input
                  id="ajuste-quantidade"
                  inputMode="decimal"
                  value={campos.quantidade}
                  onChange={(e) => alterar('quantidade', e.target.value)}
                  className="text-right tabular-nums"
                  aria-invalid={erros.quantidade ? true : undefined}
                  aria-describedby="ajuste-quantidade-ajuda"
                  autoFocus
                />
                <p id="ajuste-quantidade-ajuda" className="text-xs text-muted-foreground">
                  {ajudaQuantidadeMinima(item.saldos.recebido, item.unidade)}
                </p>
                {erros.quantidade && <FormMessage>{erros.quantidade}</FormMessage>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ajuste-preco">Preço unitário</Label>
                <Input
                  id="ajuste-preco"
                  inputMode="decimal"
                  value={campos.preco}
                  onChange={(e) => alterar('preco', e.target.value)}
                  className="text-right tabular-nums"
                  aria-invalid={erros.preco ? true : undefined}
                />
                {erros.preco && <FormMessage>{erros.preco}</FormMessage>}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void reprecificar()}
                    disabled={semPrecoVigente || reprecificando || salvando}
                  >
                    {reprecificando && <Loader2 className="animate-spin" aria-hidden="true" />}
                    Reprecificar pela tabela
                  </Button>
                  {semPrecoVigente ? (
                    <span className="text-xs text-muted-foreground">{COPIA_SEM_PRECO_VIGENTE}</span>
                  ) : (
                    <span className="text-xs tabular-nums text-muted-foreground">{rotuloReprecificar(item.precoUnitario, item.precoTabelaAtual)}</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ajuste-prazo">Prazo</Label>
                  <DatePicker id="ajuste-prazo" value={campos.previsao} onValueChange={(value) => alterar('previsao', value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ajuste-prioridade">Prioridade</Label>
                  <CampoSelect
                    id="ajuste-prioridade"
                    value={String(campos.prioridade)}
                    onValueChange={(valor) => alterar('prioridade', Number(valor) as PrioridadePv)}
                    options={PRIORIDADES.map((p) => ({ value: String(p.valor), label: p.rotulo }))}
                  />
                </div>
              </div>

              <fieldset className="flex flex-col gap-3 rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">Cor / material</legend>
                {bloqueada && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                    <AlertTriangle aria-hidden="true" />
                    <AlertDescription className="text-amber-900">{COPIA_TROCA_BLOQUEADA}</AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ajuste-material">Material interno</Label>
                  <MaterialCombobox id="ajuste-material" value={campos.material} onChange={(m) => alterar('material', m)} disabled={bloqueada} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ajuste-cor">Cor</Label>
                  <Input id="ajuste-cor" value={campos.cor} onChange={(e) => alterar('cor', e.target.value)} disabled={bloqueada} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ajuste-roteiro">Processo / roteiro</Label>
                  <RoteiroCombobox id="ajuste-roteiro" value={campos.roteiro} onChange={(r) => alterar('roteiro', r)} disabled={bloqueada} />
                </div>
                <div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setConfirmarTroca(true)} disabled={!podeTrocar || salvando}>
                    Trocar cor/material
                  </Button>
                </div>
              </fieldset>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ajuste-motivo">Motivo do ajuste (obrigatório)</Label>
                <Textarea
                  id="ajuste-motivo"
                  value={campos.motivo}
                  onChange={(e) => alterar('motivo', e.target.value)}
                  aria-invalid={erros.motivo ? true : undefined}
                />
                {erros.motivo && <FormMessage>{erros.motivo}</FormMessage>}
              </div>
              {erros.nada && <FormMessage>{erros.nada}</FormMessage>}
              {erroEnvio && <FormMessage>{erroEnvio}</FormMessage>}
            </div>

            <FormDialogFooter className="sm:flex-row sm:justify-end border-t">
              <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={salvando}>
                Voltar
              </Button>
              <Button type="submit" disabled={salvando || reprecificando}>
                {salvando && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar ajuste
              </Button>
            </FormDialogFooter>
          </form>
        </FormDialogContent>
      </FormDialog>

      <ConfirmacaoDestrutivaDialog
        aberto={confirmarTroca}
        onAbertoChange={setConfirmarTroca}
        titulo="Trocar cor/material"
        corpo={COPIA_TROCAR_MATERIAL}
        rotuloConfirmar="Trocar cor/material"
        motivoInicial={campos.motivo}
        onConfirmar={(motivo) =>
          trocarMaterial(pv.id, item.id, {
            versao: item.versao,
            motivo,
            materialId: materialAlvo?.id ?? '',
            ...(corMudou ? { cor: campos.cor.trim() || null } : {}),
            ...(roteiroMudou ? { roteiroId: campos.roteiro } : {}),
          })
        }
        onSucesso={(atualizado) => {
          onAtualizado(atualizado);
          onAbertoChange(false);
        }}
        onRecarregar={onRecarregar}
        onFalha={onFalha}
      />
    </>
  );
}
